import {
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  listAll,
  getMetadata,
  updateMetadata,
  UploadTask,
  UploadMetadata
} from 'firebase/storage';
import { storage } from './config';

// Storage paths
const STORAGE_PATHS = {
  AVATARS: 'avatars',
  EXPORTS: 'exports',
  MEDIA: 'media',
  TEMPLATES: 'templates'
};

// File type validation
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ALLOWED_DOCUMENT_TYPES = ['application/pdf', 'text/plain', 'application/json'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export interface UploadProgress {
  progress: number;
  state: 'running' | 'paused' | 'success' | 'canceled' | 'error';
  downloadURL?: string;
  error?: string;
}

class StorageService {
  // Validate file before upload
  private validateFile(file: File, allowedTypes: string[], maxSize: number = MAX_FILE_SIZE): void {
    if (!allowedTypes.includes(file.type)) {
      throw new Error(`Invalid file type. Allowed types: ${allowedTypes.join(', ')}`);
    }
    
    if (file.size > maxSize) {
      throw new Error(`File size exceeds ${maxSize / (1024 * 1024)}MB limit`);
    }
  }

  // Generate unique filename
  private generateFileName(originalName: string, userId: string): string {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 9);
    const extension = originalName.split('.').pop();
    return `${userId}_${timestamp}_${randomString}.${extension}`;
  }

  // Upload file with progress tracking
  uploadWithProgress(
    file: File,
    path: string,
    metadata?: UploadMetadata,
    onProgress?: (progress: UploadProgress) => void
  ): UploadTask {
    const storageRef = ref(storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file, metadata);

    if (onProgress) {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress({
            progress,
            state: snapshot.state as UploadProgress['state']
          });
        },
        (error) => {
          onProgress({
            progress: 0,
            state: 'error',
            error: error.message
          });
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          onProgress({
            progress: 100,
            state: 'success',
            downloadURL
          });
        }
      );
    }

    return uploadTask;
  }

  // Simple upload without progress tracking
  async upload(
    file: File,
    path: string,
    metadata?: UploadMetadata
  ): Promise<string> {
    try {
      const storageRef = ref(storage, path);
      const snapshot = await uploadBytes(storageRef, file, metadata);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error: any) {
      throw new Error(`Failed to upload file: ${error.message}`);
    }
  }

  // Delete file
  async delete(path: string): Promise<void> {
    try {
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
    } catch (error: any) {
      throw new Error(`Failed to delete file: ${error.message}`);
    }
  }

  // Get download URL
  async getDownloadURL(path: string): Promise<string> {
    try {
      const storageRef = ref(storage, path);
      return await getDownloadURL(storageRef);
    } catch (error: any) {
      throw new Error(`Failed to get download URL: ${error.message}`);
    }
  }

  // List files in a directory
  async listFiles(path: string): Promise<{ name: string; url: string; metadata: any }[]> {
    try {
      const storageRef = ref(storage, path);
      const result = await listAll(storageRef);
      
      const files = await Promise.all(
        result.items.map(async (itemRef) => {
          const url = await getDownloadURL(itemRef);
          const metadata = await getMetadata(itemRef);
          return {
            name: itemRef.name,
            url,
            metadata
          };
        })
      );
      
      return files;
    } catch (error: any) {
      throw new Error(`Failed to list files: ${error.message}`);
    }
  }

  // Update file metadata
  async updateMetadata(path: string, metadata: UploadMetadata): Promise<void> {
    try {
      const storageRef = ref(storage, path);
      await updateMetadata(storageRef, metadata);
    } catch (error: any) {
      throw new Error(`Failed to update metadata: ${error.message}`);
    }
  }
}

// Specific storage services
export const avatarStorage = {
  // Upload user avatar
  upload: async (
    file: File,
    userId: string,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<string> => {
    storageService.validateFile(file, ALLOWED_IMAGE_TYPES, 5 * 1024 * 1024); // 5MB limit for avatars
    
    const fileName = storageService.generateFileName(file.name, userId);
    const path = `${STORAGE_PATHS.AVATARS}/${userId}/${fileName}`;
    
    if (onProgress) {
      return new Promise((resolve, reject) => {
        const uploadTask = storageService.uploadWithProgress(
          file,
          path,
          { contentType: file.type },
          (progress) => {
            onProgress(progress);
            if (progress.state === 'success' && progress.downloadURL) {
              resolve(progress.downloadURL);
            } else if (progress.state === 'error') {
              reject(new Error(progress.error));
            }
          }
        );
      });
    } else {
      return storageService.upload(file, path, { contentType: file.type });
    }
  },

  // Delete user avatar
  delete: async (userId: string, fileName: string) => {
    const path = `${STORAGE_PATHS.AVATARS}/${userId}/${fileName}`;
    return storageService.delete(path);
  },

  // Get avatar URL
  getURL: async (userId: string, fileName: string) => {
    const path = `${STORAGE_PATHS.AVATARS}/${userId}/${fileName}`;
    return storageService.getDownloadURL(path);
  }
};

export const exportStorage = {
  // Upload export file
  upload: async (
    file: File | Blob,
    userId: string,
    fileName: string,
    metadata?: UploadMetadata
  ): Promise<string> => {
    const path = `${STORAGE_PATHS.EXPORTS}/${userId}/${fileName}`;
    
    if (file instanceof File) {
      storageService.validateFile(file, [...ALLOWED_DOCUMENT_TYPES, ...ALLOWED_IMAGE_TYPES]);
      return storageService.upload(file, path, metadata);
    } else {
      // For Blob (generated files)
      const storageRef = ref(storage, path);
      const snapshot = await uploadBytes(storageRef, file, metadata);
      return getDownloadURL(snapshot.ref);
    }
  },

  // List user's exports
  list: async (userId: string) => {
    const path = `${STORAGE_PATHS.EXPORTS}/${userId}`;
    return storageService.listFiles(path);
  },

  // Delete export
  delete: async (userId: string, fileName: string) => {
    const path = `${STORAGE_PATHS.EXPORTS}/${userId}/${fileName}`;
    return storageService.delete(path);
  },

  // Create and upload JSON export
  uploadJSON: async (data: any, userId: string, fileName: string): Promise<string> => {
    const jsonBlob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    return exportStorage.upload(jsonBlob, userId, `${fileName}.json`, {
      contentType: 'application/json',
      customMetadata: {
        createdAt: new Date().toISOString(),
        type: 'prompt-export'
      }
    });
  },

  // Create and upload text export
  uploadText: async (text: string, userId: string, fileName: string): Promise<string> => {
    const textBlob = new Blob([text], { type: 'text/plain' });
    return exportStorage.upload(textBlob, userId, `${fileName}.txt`, {
      contentType: 'text/plain',
      customMetadata: {
        createdAt: new Date().toISOString(),
        type: 'prompt-export'
      }
    });
  }
};

export const mediaStorage = {
  // Upload generated media
  upload: async (
    file: File | Blob,
    promptId: string,
    metadata?: UploadMetadata
  ): Promise<string> => {
    const fileName = `${promptId}_${Date.now()}`;
    const path = `${STORAGE_PATHS.MEDIA}/${promptId}/${fileName}`;
    
    if (file instanceof File) {
      storageService.validateFile(file, ALLOWED_IMAGE_TYPES);
      return storageService.upload(file, path, metadata);
    } else {
      const storageRef = ref(storage, path);
      const snapshot = await uploadBytes(storageRef, file, metadata);
      return getDownloadURL(snapshot.ref);
    }
  },

  // List media for a prompt
  list: async (promptId: string) => {
    const path = `${STORAGE_PATHS.MEDIA}/${promptId}`;
    return storageService.listFiles(path);
  },

  // Delete media
  delete: async (promptId: string, fileName: string) => {
    const path = `${STORAGE_PATHS.MEDIA}/${promptId}/${fileName}`;
    return storageService.delete(path);
  }
};

// Create download link with expiry
export const createTemporaryDownloadLink = async (
  path: string,
  expiryHours: number = 24
): Promise<{ url: string; expiresAt: Date }> => {
  try {
    const url = await storageService.getDownloadURL(path);
    const expiresAt = new Date(Date.now() + expiryHours * 60 * 60 * 1000);
    
    // Note: Firebase Storage URLs don't expire by default
    // For true temporary URLs, you'd need to use Firebase Functions
    // to generate signed URLs with expiry
    
    return { url, expiresAt };
  } catch (error: any) {
    throw new Error(`Failed to create download link: ${error.message}`);
  }
};

// Helper to convert base64 to File
export const base64ToFile = (base64: string, fileName: string): File => {
  const arr = base64.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  return new File([u8arr], fileName, { type: mime });
};

// Export the main service instance
const storageService = new StorageService();
export default storageService;